import actionTypes from 'constants/actionTypes'
import type { ShapesByEntityMap, ShapesAllMap } from 'types/shapes'

interface ByEntityAction {
  type?: string
  payload?: {
    id?: string
    entityIds?: string[]
  }
  _shapesAll?: ShapesAllMap
}

const removeShapeFromIndex = (state: ShapesByEntityMap, shapeId: string, entityIds: string[]): ShapesByEntityMap => {
  const updated = { ...state }
  for (const entityId of entityIds) {
    const current = updated[entityId]
    if (!current) continue
    const filtered = current.filter((id) => id !== shapeId)
    if (filtered.length === 0) {
      delete updated[entityId]
    } else {
      updated[entityId] = filtered
    }
  }
  return updated
}

const addShapeToIndex = (state: ShapesByEntityMap, shapeId: string, entityIds: string[]): ShapesByEntityMap => {
  const updated = { ...state }
  for (const entityId of entityIds) {
    const current = updated[entityId]
    if (!current) {
      updated[entityId] = [shapeId]
    } else if (!current.includes(shapeId)) {
      updated[entityId] = [...current, shapeId]
    }
  }
  return updated
}

const byEntity = (state: ShapesByEntityMap = {}, action: ByEntityAction): ShapesByEntityMap => {
  switch (action.type) {
    case actionTypes.SHAPES.ADD: {
      const shapeId = action.payload?.id
      const entityIds = action.payload?.entityIds
      if (!shapeId || !entityIds) return state
      return addShapeToIndex(state, shapeId, entityIds)
    }
    case actionTypes.SHAPES.REMOVE: {
      const shapeId = action.payload?.id
      if (!shapeId) return state
      // Find all entities that reference this shape and remove them
      const entitiesToClean: string[] = []
      for (const [entityId, shapeIds] of Object.entries(state)) {
        if (shapeIds.includes(shapeId)) {
          entitiesToClean.push(entityId)
        }
      }
      if (entitiesToClean.length === 0) return state
      return removeShapeFromIndex(state, shapeId, entitiesToClean)
    }
    case actionTypes.SHAPES.UPDATE: {
      const shapeId = action.payload?.id
      const newEntityIds = action.payload?.entityIds
      if (!shapeId) return state
      // If entityIds didn't change, nothing to do
      if (newEntityIds === undefined) return state
      // Remove old entries and add new ones
      const oldEntities: string[] = []
      for (const [entityId, shapeIds] of Object.entries(state)) {
        if (shapeIds.includes(shapeId)) {
          oldEntities.push(entityId)
        }
      }
      const cleaned = removeShapeFromIndex(state, shapeId, oldEntities)
      return addShapeToIndex(cleaned, shapeId, newEntityIds)
    }
    default:
      return state
  }
}

export default byEntity
